import { Box, Avatar, Typography, Button, Paper, Divider, Breadcrumbs, Link, IconButton } from '@mui/material'
import { Link as RouterLink } from 'react-router'
import { themeColors } from '../../app/layout/theme'

const ThinBackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

interface BreadcrumbItem {
  label: string
  to?: string
}

interface StatItem {
  label: string
  value: string | number
  subtext?: string
}

interface EntityDetailHeaderProps {
  title: string
  breadcrumbs: BreadcrumbItem[]
  avatarInitials: string
  badge?: React.ReactNode
  subtitles: { icon: React.ReactNode; text: React.ReactNode }[]
  onBack?: () => void
  actions?: React.ReactNode
  stats?: StatItem[]
}

export const EntityDetailHeader = ({
  title,
  breadcrumbs,
  avatarInitials,
  badge,
  subtitles,
  onBack,
  actions,
  stats,
}: EntityDetailHeaderProps) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Top Breadcrumbs & Back */}
      <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        {onBack && (
          <IconButton size="small" onClick={onBack} sx={{ p: 0.5, mr: 0.5 }}>
            <ThinBackIcon />
          </IconButton>
        )}
        <Breadcrumbs separator="/" sx={{ '& .MuiBreadcrumbs-separator': { fontSize: '14px', mx: 0.5, color: 'text.secondary' } }}>
          {breadcrumbs.map((b, i) => (
            <Typography
              key={i}
              fontSize="14px"
              fontWeight={i === breadcrumbs.length - 1 ? 700 : 400}
              color={i === breadcrumbs.length - 1 ? 'text.primary' : 'text.secondary'}
            >
              {b.to ? (
                <Link component={RouterLink} to={b.to} underline="hover" sx={{ color: '#6B7280', '&:hover': { color: themeColors.primary } }}>
                  {b.label}
                </Link>
              ) : b.label}
            </Typography>
          ))}
        </Breadcrumbs>
      </Box>

      {/* Main Header Card */}
      <Paper elevation={0} sx={{
        mx: 3,
        width: 'auto',
        height: '236px',
        borderRadius: '16px',
        border: `1px solid ${themeColors.border}`,
        overflow: 'hidden',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Row 1: Purple Gradient (60px) */}
        <Box sx={{
          height: '60px',
          background: 'linear-gradient(90deg, #E0E7FF 0%, #C7D2FE 50%, #E0E7FF 100%)',
          flexShrink: 0
        }} />

        {/* Row 2: Content Area (82px) */}
        <Box sx={{ height: '82px', px: '24px', bottom: '10px', position: 'relative', display: 'flex', gap: '16px', alignItems: 'center', flexShrink: 0 }}>
          {/* Overlapping Avatar */}
          <Avatar sx={{
            width: 64,
            height: 64,
            bgcolor: '#F3F4F6',
            color: themeColors.primary,
            fontSize: '20px',
            fontWeight: 700,
            border: '4px solid #fff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            position: 'absolute',
            top: '-6px',
            left: '20px',
            zIndex: 10,
            borderRadius: '67px',
            opacity: 1
          }}>
            {avatarInitials}
          </Avatar>

          <Box sx={{ ml: '70px', display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ fontSize: '20px' }}>
                  {title}
                </Typography>
                {badge}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {subtitles.map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', fontSize: '16px', opacity: 0.7 }}>{s.icon}</Box>
                    <Typography fontSize="12.5px" fontWeight={500}>{s.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {actions}
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Row 3: Stats Row (94px) */}
        <Box sx={{ flex: 1, height: '94px', display: 'flex', bgcolor: '#fff' }}>
          {stats?.map((stat, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                px: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderRight: i === stats.length - 1 ? 'none' : `1px solid ${themeColors.border}`,
                '&:hover': { bgcolor: '#fafafa' }
              }}
            >
              <Typography fontSize="12px" color="text.secondary" fontWeight={500} mb={0.5}>
                {stat.label}
              </Typography>
              <Typography fontSize="18px" fontWeight={800} color="text.primary" lineHeight={1.1}>
                {stat.value}
              </Typography>
              {stat.subtext && (
                <Typography fontSize="11px" color="text.secondary" mt={0.25}>
                  {stat.subtext}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  )
}
